// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import HeaderClickable, { HeaderClickableStyle } from "@guestsupply/App/Components/Header/HeaderClickable";
import MiniCart from "@guestsupply/App/Components/MiniCart/MiniCart";
import { TippyPopupStyle } from "@guestsupply/App/Components/Tippy/Style";
import TippyPopup from "@guestsupply/App/Components/Tippy/TippyPopup";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { xIsAuthenticated } from "@guestsupply/App/Store/Selectors/GeneralSelectors";
import Cart from "@guestsupply/XDL/Icons/Cart";
import CartFocused from "@guestsupply/XDL/Icons/CartFocused";
import { removeScrollbarGutter, xClassNames } from "@guestsupply/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFlyoutDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetFlyoutDrawerIsOpen";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconWrapper } from "@insite/mobius/Icon";
import ShoppingBag from "@insite/mobius/Icons/ShoppingBag";
import ShoppingCart from "@insite/mobius/Icons/ShoppingCart";
import Truck from "@insite/mobius/Icons/Truck";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, withTheme } from "styled-components";
/* eslint-disable spire/fenced-imports */
import xngGaToolBarNavigationEvent from "@guestsupply/App/Handlers/GoogleAnalytics/ToolBarNavigation/ToolBarNavigationEvent";
import translate from "@insite/client-framework/Translate";
import * as ariaAppHider from "@insite/mobius/Overlay/helpers/ariaAppHider";
import { hideAll } from "tippy.js";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
}

const mapStateToProps = (state: ApplicationState) => {
    const cartState = getCurrentCartState(state);
    return {
        shouldLoadCart: !getCurrentCartState(state).value && !getCurrentCartState(state).isLoading,
        totalCountDisplay: cartState.value?.totalCountDisplay,
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        // XNG-Changes
        orderSubTotalDisplay: cartState.value?.orderSubTotalDisplay,
        currencySymbol: cartState.value?.currencySymbol || state.context.session.currency?.currencySymbol || "$",
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
        isAuthenticated: xIsAuthenticated(state),
        lineCount: cartState.value?.lineCount,
        isSticky: (state as CustomAppState).xHeader.isSticky,
    };
};

const mapDispatchToProps = {
    loadCurrentCart,
    // XNG-Change
    setNavDrawerIsOpen,
    setFlyoutDrawerIsOpen,
    // xng-change: add google event
    xngGaToolBarNavigationEvent,
};

export interface CartLinkStyles {
    routerLink?: LinkPresentationProps;
    desktopCountText?: TypographyPresentationProps;
    mobileCountText?: TypographyPresentationProps;
    compactCountText?: TypographyPresentationProps;
    // XNG-Change
    headerClickable?: HeaderClickableStyle;
    tippyPopup?: TippyPopupStyle;
    lineCountWrapper?: InjectableCss;
    lineCount?: InjectableCss;
}

export const cartLinkStyles: CartLinkStyles = {
    routerLink: {
        typographyProps: {
            variant: "headerSecondary",
        },
        icon: {
            iconProps: {
                size: 24,
            },
        },
        color: "secondary.contrast",
        css: css`
            height: 50px;
            padding: 0 4px 0 10px;
        `,
    },
    compactCountText: {
        css: css`
            font-weight: normal;
        `,
    },
};

const iconMap = {
    cart: Cart,
    truck: Truck,
    bag: ShoppingBag,
    none: undefined,
} as const;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: "both" | "label" | "icon";
        [fields.icon]: keyof typeof iconMap;
    };
    isCompactHeaderCart?: boolean;
    theme: BaseTheme;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

/** Reloading the cart makes `totalCountDisplay` temporarily unavailable, so retain the previous value for better UX. */
let retainedTotalCountToDisplay: number | undefined;

const CartLink: FC<Props> = ({
    shouldLoadCart,
    cartUrl,
    totalCountDisplay,
    fields: { visibilityState, icon: selectedIcon },
    loadCurrentCart,
    isCompactHeaderCart,
    theme,
    orderSubTotalDisplay,
    currencySymbol,
    isTabletScreen,
    setNavDrawerIsOpen,
    setFlyoutDrawerIsOpen,
    isAuthenticated,
    lineCount,
    isSticky,
    // xng-change: add google event
    xngGaToolBarNavigationEvent,
}) => {
    const [isPopupShown, setIsPopupShown] = useState(false);

    useEffect(() => {
        if (shouldLoadCart) {
            loadCurrentCart({ shouldLoadFullCart: false, replaceCart: false });
        }
    }, [shouldLoadCart]);

    const styles = useMergeStyles("cartLink", cartLinkStyles);
    const history = useHistory();

    const showIcon = visibilityState !== "label";
    const showLabel = visibilityState !== "icon";

    const openCartPageHandler = () => {
        setTimeout(() => {
            // XNG-CHANGES : To make sure all tippy are hidden and scrollbar is present
            hideAll();
            removeScrollbarGutter();
            ariaAppHider.show(document.body);
        }, 500);

        if (!cartUrl) {
            return;
        }
        history.push(cartUrl);
    };

    if (typeof totalCountDisplay !== "undefined") {
        retainedTotalCountToDisplay = totalCountDisplay;
    }

    const icon = iconMap[(showIcon && selectedIcon) || "none"];

    const { routerLink } = styles;

    if (isCompactHeaderCart) {
        return (
            <Link
                {...routerLink}
                color={theme.header.linkColor}
                href={cartUrl}
                icon={{ ...routerLink?.icon, iconProps: { src: icon, ...routerLink?.icon?.iconProps } }}
                data-test-selector="cartLink"
                aria-label={siteMessage("Header_CartCount")}
            >
                <Typography as="span" {...styles.compactCountText}>
                    {retainedTotalCountToDisplay}
                </Typography>
                <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
            </Link>
        );
    }

    // XNG-Change
    const headerClickableElement = (
        <HeaderClickable
            variant="cart"
            onClick={openCartPageHandler}
            href={cartUrl}
            iconProps={icon ? { src: CartFocused } : undefined}
            data-test-selector="cartLink"
            title={showLabel ? `${orderSubTotalDisplay ?? `${currencySymbol}0.00`}` : ""}
            extendedStyles={styles.headerClickable}
            className={`${isPopupShown && "minicart-open"}`}
        >
            {!!lineCount && (
                <StyledWrapper {...styles.lineCountWrapper} className={xClassNames.append([isSticky && "sticky"])}>
                    <StyledWrapper {...styles.lineCount} className={`line-count ${isPopupShown && "cart-opened"}`}>
                        {(lineCount || 0) > 99 ? "99+" : lineCount}
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </HeaderClickable>
    );

    const closeNavigationDrawer = () => {
        setFlyoutDrawerIsOpen({ isOpen: false });
        setTimeout(() => setFlyoutDrawerIsOpen({ isOpen: undefined }), 300);
        setNavDrawerIsOpen({ navDrawerIsOpen: false });
        setTimeout(() => setNavDrawerIsOpen({ navDrawerIsOpen: undefined }), 300);
    };

    // XNG-Change Hide MiniCart on tablet
    if (isTabletScreen) {
        return headerClickableElement;
    }

    return (
        <TippyPopup
            extendedStyles={styles.tippyPopup}
            content={<MiniCart isPopupShown={isPopupShown} />}
            arrow={true}
            maxWidth={603}
            placement="bottom-end"
            type="menu"
            offset={[50, 12]}
            showCloseButton={false}
            onTriggerPopup={isOpen => {
                setIsPopupShown(isOpen);
                // xng-change: add google event
                xngGaToolBarNavigationEvent({
                    clickText: translate("Cart"),
                });
            }}
            appendTo="parent"
            onShow={instance => {
                closeNavigationDrawer();
            }}
            popperOptions={{
                strategy: "absolute",
                modifiers: [
                    {
                        name: "preventOverflow",
                        options: {
                            padding: 0,
                        },
                    },
                    {
                        name: "computeStyles",
                        options: {
                            adaptive: true,
                            roundOffsets: ({ x, y }: any) => ({
                                x,
                                y,
                            }),
                        },
                    },
                ],
            }}
        >
            {headerClickableElement}
        </TippyPopup>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(CartLink)),
    definition: {
        displayName: "Cart",
        icon: "cart-shopping",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Show Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label Only",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon Only",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "Shopping Cart Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "None",
                        value: "none",
                    },
                    {
                        displayName: "Bag",
                        value: "bag",
                    },
                    {
                        displayName: "Cart",
                        value: "cart",
                    },
                    {
                        displayName: "Truck",
                        value: "truck",
                    },
                ],
                defaultValue: "cart",
                fieldType: "General",
                sortOrder: 2,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
